import React = require("react");
import {CardBox} from "../../../components/CardBox";
import {Title} from "../../../components/Title";
import {PrettyPrinter} from "../../../components/Utils/PrettyPrinter";
import {MarketingConfig, UNITS_UNIT_OF_MEASURE_CODE} from "../data/MarketingConfig";
import UnitOfMeasure = MarketingConfig.UnitOfMeasure;
import {MarketingData} from "../data/MarketingData";
import "./MarketingSalesWidget.scss";
import MarketingDataConverter = MarketingData.MarketingDataConverter;
import {VeBoxCard} from "../../../components/card/VeBoxCard";
import {Display} from "../../../components/Display";

interface Sale {
    title: string;
    quantity: number;
    unitOfMeasure: string;
}

interface MarketingSalesWidgetProps {
    period?: string;
    yearAgoPeriod?: string;
    twoYearsAgoPeriod?: string;
    displayTwoYearsAgo?: boolean;
    title: string;
    titleSalesTotals: string;
    titleLocationAverage: string;
    titleSelectionAverage: string;
    titleEquipmentAverage: string;
    unitOfMeasure: UnitOfMeasure;
    sales: Sale;
    salesYearAgo: Sale;
    salesTwoYearsAgo: Sale;
    locationAvg: Sale;
    selectionAvg: Sale;
    equipmentAvg: Sale;
    locationAvgYearAgo: Sale;
    selectionAvgYearAgo: Sale;
    equipmentAvgYearAgo: Sale;
    locationAvgTwoYearsAgo: Sale;
    selectionAvgTwoYearsAgo: Sale;
    equipmentAvgTwoYearsAgo: Sale;
}

export const MarketingSalesWidget: React.SFC<MarketingSalesWidgetProps> = (props) => {

    /** Background color for the total sales card box. */
    const SALES_TOTALS_COLOR: string = "rgba(24, 138, 226, 0.2)";
    /** Background color for the average sales card box. */
    const SALES_AVG_COLOR: string = "rgba(67, 167, 69, 0.2)";

    let salesTotals;
    {
        const title = props.titleSalesTotals;
        let value: string;
        let footer = null;
        value = PrettyPrinter.salesQuantity(props.sales.quantity, props.sales.unitOfMeasure);
        if (props.salesYearAgo) {
            footer = (
                <div>
                    <p>
                        {props.yearAgoPeriod + ": " +
                        PrettyPrinter.salesQuantity(props.salesYearAgo.quantity, props.salesYearAgo.unitOfMeasure)}
                    </p>
                    {props.displayTwoYearsAgo ?
                        <p className={"marketing-two-years-ago-text"}>
                            {props.twoYearsAgoPeriod + ": " +
                            PrettyPrinter.salesQuantity(
                                props.salesTwoYearsAgo.quantity,
                                props.salesTwoYearsAgo.unitOfMeasure,
                            )}
                        </p>
                        : null
                    }
                </div>
            );
        }

        salesTotals = (
            <CardBox
                title={title}
                value={value}
                footer={footer}
                backgroundColor={SALES_TOTALS_COLOR}
            />
        );
    }
    let locationAvg;
    {
        const title: string = props.titleLocationAverage;
        let value: string;
        let valueUp: boolean = null;
        let footer = null;

        value = PrettyPrinter.salesQuantity(props.locationAvg.quantity, props.locationAvg.unitOfMeasure);
        const valueDiff = props.locationAvg.quantity - props.locationAvgYearAgo.quantity;
        if (valueDiff > 0) {
            valueUp = true;
        } else if (valueDiff < 0) {
            valueUp = false;
        }
        if (props.locationAvgYearAgo) {
            footer = (
                <div>
                    <p>{props.yearAgoPeriod + ": " +
                    PrettyPrinter.salesQuantity(
                        props.locationAvgYearAgo.quantity,
                        props.locationAvgYearAgo.unitOfMeasure,
                    )}</p>
                    {props.displayTwoYearsAgo ?
                        <p className={"marketing-two-years-ago-text"}>{props.twoYearsAgoPeriod + ": " +
                        PrettyPrinter.salesQuantity(
                            props.locationAvgTwoYearsAgo.quantity,
                            props.locationAvgTwoYearsAgo.unitOfMeasure,
                        )}</p>
                        : null
                    }
                </div>
            );
        }
        locationAvg = (
            <CardBox
                title={title}
                value={value}
                valueUp={valueUp}
                footer={footer}
                backgroundColor={SALES_AVG_COLOR}
            />
        );
    }
    let selectionAvg;
    {
        const title: string = props.titleSelectionAverage;
        let value: string;
        let valueUp: boolean = null;
        let footer: JSX.Element = null;

        value = PrettyPrinter.salesQuantity(props.selectionAvg.quantity, props.selectionAvg.unitOfMeasure);
        const valueDiff = props.selectionAvg.quantity - props.selectionAvgYearAgo.quantity;
        if (valueDiff > 0) {
            valueUp = true;
        } else if (valueDiff < 0) {
            valueUp = false;
        }
        if (props.selectionAvgYearAgo) {
            footer = (
                <div>
                    <p>{props.yearAgoPeriod + ": " +
                    PrettyPrinter.salesQuantity(
                        props.selectionAvgYearAgo.quantity,
                        props.selectionAvgYearAgo.unitOfMeasure,
                    )}</p>
                    {props.displayTwoYearsAgo ?
                        <p className={"marketing-two-years-ago-text"}>{props.twoYearsAgoPeriod + ": " +
                        PrettyPrinter.salesQuantity(
                            props.selectionAvgTwoYearsAgo.quantity,
                            props.selectionAvgTwoYearsAgo.unitOfMeasure,
                        )}</p>
                        : null
                    }
                </div>
            );
        }

        selectionAvg = (
            <CardBox
                title={title}
                value={value}
                valueUp={valueUp}
                footer={footer}
                backgroundColor={SALES_AVG_COLOR}
            />
        );
    }
    let equipmentAvg;
    {
        const title: string = props.titleEquipmentAverage;
        let value: string;
        let valueUp: boolean = null;
        let footer: JSX.Element = null;
        value = PrettyPrinter.salesQuantity(props.equipmentAvg.quantity, props.equipmentAvg.unitOfMeasure);
        const valueDiff = props.equipmentAvg.quantity - props.equipmentAvgYearAgo.quantity;
        if (valueDiff > 0) {
            valueUp = true;
        } else if (valueDiff < 0) {
            valueUp = false;
        }
        if (props.equipmentAvgYearAgo) {
            footer = (
                <div>
                    <p>
                        {props.yearAgoPeriod + ": " +
                        PrettyPrinter.salesQuantity(
                            props.equipmentAvgYearAgo.quantity,
                            props.equipmentAvgYearAgo.unitOfMeasure,
                        )}
                    </p>
                    {props.displayTwoYearsAgo ?
                        <p className={"marketing-two-years-ago-text"}>
                            {props.twoYearsAgoPeriod + ": " +
                            PrettyPrinter.salesQuantity(
                                props.equipmentAvgTwoYearsAgo.quantity,
                                props.equipmentAvgTwoYearsAgo.unitOfMeasure,
                            )}
                        </p>
                        : null
                    }
                </div>
            );

        }

        equipmentAvg = (
            <CardBox
                title={title}
                value={value}
                valueUp={valueUp}
                footer={footer}
                backgroundColor={SALES_AVG_COLOR}
            />
        );
    }
    return (
        <VeBoxCard className="marketing-widget marketing-widget-2x2 marketing-widget-sales no-margin">
            <VeBoxCard.Header title={props.title}/>
            <VeBoxCard.Body className={"grid-table"}>
                <div className={"grid-cell"}>
                    {salesTotals}
                </div>
                <div className={"grid-cell"}>
                    {locationAvg}
                </div>
                <div className={"grid-cell"}>
                    {equipmentAvg}
                </div>
                <Display isVisible={!MarketingDataConverter.isUnitsUnitOfMeasure(props.unitOfMeasure)}>
                    <div className={"grid-cell"}>
                        {selectionAvg}
                    </div>
                </Display>
            </VeBoxCard.Body>
        </VeBoxCard>
    );
};
